import React, { useEffect, useState } from "react";
import { Box, Button, Typography, IconButton } from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description"; // General file icon
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"; // PDF icon
import ArticleIcon from "@mui/icons-material/Article"; // DOC icon
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"; // Excel icon
import axios from "axios";
import { api } from "../../../utils/api";
import { formatTimestamp } from "../../../utils/formatTimeStamp";

const BulkFileUpload = ({ handleChange, handleClear, filesList, index }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    console.log("filesList", filesList);
    if (filesList && filesList.length > 0) {
      setUploadedFiles(filesList); // Initialize with the filesList passed from the parent
    } else {
      setUploadedFiles([]);
    }
  }, [filesList]);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (files.length === 0) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        alert("Only .pdf, .doc, .docx, .xls, .xlsx files are allowed!");
        e.target.value = null;
        return;
      }
    }

    const formData = new FormData();
    for (let file of files) {
      // Generate unique file name with timestamp
      const timestamp = Date.now();
      const formattedTimestamp = formatTimestamp(timestamp);
      const originalFileName = file.name.split(".").slice(0, -1).join(".");
      const fileExtension = file.name.split(".").pop();
      const uniqueFileName = `${originalFileName}_${formattedTimestamp}.${fileExtension}`;
      const uniqueFile = new File([file], uniqueFileName, { type: file.type });
      
      formData.append("files", uniqueFile);
    }

    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await axios.post(`${api}/cloud/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const files = response.data.urls || [];
      setUploadedFiles(files);
      
      // Handle both common documents (no index) and individual documents (with index)
      if (index !== undefined) {
        handleChange(index, files);
      } else {
        handleChange(files);
      }

    } catch (error) {
      console.error("Error uploading files:", error);
      setErrorMessage("Failed to upload the files. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileDelete = () => {
    setUploadedFiles([]); // Clear the uploaded file
    
    // Handle both common documents (no index) and individual documents (with index)
    if (index !== undefined) {
      handleClear(index);
    } else {
      handleClear();
    }
  };

  const getFileIcon = (fileType) => {
    console.log("log files", fileType);
    if (fileType?.includes("pdf")) {
      return <PictureAsPdfIcon sx={{ color: "red", fontSize: "20px" }} />;
    }
    if (fileType?.includes("docx") || fileType?.includes("doc")) {
      return <ArticleIcon sx={{ color: "blue", fontSize: "20px" }} />;
    }
    if (
      fileType?.includes("xlsx") ||
      fileType?.includes("xls") ||
      fileType?.includes("sheet")
    ) {
      return <InsertDriveFileIcon sx={{ color: "green", fontSize: "20px" }} />;
    }
    return <DescriptionIcon sx={{ color: "gray", fontSize: "20px" }} />;
  };

  return (
    <Box>
      {uploadedFiles.length === 0 ? (
        <Box>
          <Button
            variant="outlined"
            component="label"
            sx={{
              padding: "6px 12px",
              borderRadius: "4px",
              fontWeight: "bold",
            }}
          >
            <FileUploadIcon sx={{ fontSize: "20px" }} />
            Upload
            <input
              type="file"
              hidden
              accept=".doc,.docx,.pdf,.xls,.xlsx"
              onChange={handleFileUpload}
              disabled={isLoading}
            />
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            position: "relative",
            width: "6.2rem", // Adjust the width of the container
            height: "40px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "8px 20px 8px 8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f9f9f9",
            overflow: "hidden", // Prevent overflow
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleFileDelete}
            sx={{
              position: "absolute",
              // top: "4px",
              right: "2px",
              padding: "4px",
            }}
          >
            <CloseIcon sx={{ fontSize: "15px" }} />
          </IconButton>

          {/* File Information */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "12%",
              flex: 1,
            }}
          >
        {getFileIcon(uploadedFiles?.[0])}
            <Typography
              sx={{
                marginLeft: "4px",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "12px",
                color: "#333",
              }}
            >
              {(() => {
                const fileName = decodeURIComponent(uploadedFiles?.[0].split("/").pop());
                return fileName.replace(/^\d+_/, '');
              })()}
            </Typography>
          </Box>
        </Box>
      )}
      {errorMessage && (
        <Typography sx={{ color: "red" }}>{errorMessage}</Typography>
      )}
    </Box>
  );
};

export default BulkFileUpload;
